/**
 * Usage for the metered third-party APIs — Google Ads and GNews.
 *
 * The third panel of the same family as TokenUsageReport (AI) and
 * NetworkRequestReport (crawler), and deliberately the same controls: a period
 * pager, a filter, a draggable chart, and a table whose rows expand into the
 * individual calls behind them. Period arithmetic is shared in ./usagePeriods.
 *
 * What is different here is WHO. One GNews key and one Google Ads manager
 * account serve the whole install, so "we are near the cap" is only actionable
 * once you know whose work is filling it. An admin viewing Everyone gets a
 * per-user breakdown; nobody else sees that section, because scoped to one
 * person it would just restate the header.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ReferenceArea, ResponsiveContainer,
} from 'recharts';
import api from '../services/api';
import type { ApiUsageReport, ApiUsageTimeseries, ApiUsageCallRow } from '../types';
import UsageUserFilter, { ALL_USERS } from '../components/UsageUserFilter';
import {
  brushToDateRange,
  bucketsSoFar,
  formatPeriodHeader,
  isCurrentOrFuturePeriod,
  shiftPeriod,
  type Period,
} from './usagePeriods';

// ExternalApiProvider on the backend. '' = all.
const PROVIDERS: { value: string; label: string }[] = [
  { value: 'GoogleAds', label: 'Google Ads' },
  { value: 'GNews', label: 'News (GNews)' },
];

const providerLabel = (value: string) =>
  PROVIDERS.find((p) => p.value === value)?.label ?? value;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg px-3 py-2 text-sm">
      <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">{label}</div>
      {payload.map((entry: { name: string; value: number; color: string }, i: number) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: entry.color }} />
          <span className="text-gray-600 dark:text-gray-300">{entry.name}:</span>
          <span className="font-mono font-medium text-gray-900 dark:text-gray-100">{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function ApiUsageReportPage() {
  const [period, setPeriod] = useState<Period>('month');
  const [periodDate, setPeriodDate] = useState<string | undefined>(undefined);
  const [provider, setProvider] = useState<string>('');
  // '' = my own usage. Admin-only; see UsageUserFilter.
  const [userScope, setUserScope] = useState<string>('');
  const [report, setReport] = useState<ApiUsageReport | null>(null);
  const [timeseries, setTimeseries] = useState<ApiUsageTimeseries | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [brushRange, setBrushRange] = useState<[number, number] | null>(null);
  const [dragStart, setDragStart] = useState<string | null>(null);
  const [dragEnd, setDragEnd] = useState<string | null>(null);
  // The expanded table row, keyed "provider:operation".
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [callDetails, setCallDetails] = useState<ApiUsageCallRow[] | null>(null);
  const [callDetailsLoading, setCallDetailsLoading] = useState(false);
  const [callDetailsTotal, setCallDetailsTotal] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [reportData, tsData] = await Promise.all([
        api.getApiUsageReport(period, periodDate, provider || undefined, userScope || undefined),
        api.getApiUsageTimeseries(period, periodDate, provider || undefined, userScope || undefined),
      ]);
      setReport(reportData);
      setTimeseries(tsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load API usage report');
    } finally {
      setLoading(false);
    }
  }, [period, periodDate, provider, userScope]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const clearSelection = () => {
    setBrushRange(null);
    setExpandedKey(null);
    setCallDetails(null);
  };

  const handlePeriodChange = (p: Period) => {
    setPeriod(p);
    setPeriodDate(undefined);
    clearSelection();
  };

  const handlePrev = () => {
    if (!report?.period_start) return;
    setPeriodDate(shiftPeriod(report.period_start, period, -1));
    clearSelection();
  };

  const handleNext = () => {
    if (!report?.period_start) return;
    setPeriodDate(shiftPeriod(report.period_start, period, 1));
    clearSelection();
  };

  const nextDisabled = report?.period_start
    ? isCurrentOrFuturePeriod(report.period_start, period)
    : true;

  const allBuckets = React.useMemo(
    () => bucketsSoFar(timeseries?.buckets ?? [], period, report?.period_start),
    [timeseries, report?.period_start, period],
  );

  const chartData = brushRange
    ? allBuckets.slice(brushRange[0], brushRange[1] + 1)
    : allBuckets;

  const hasTimeseries = timeseries && timeseries.buckets.length > 0;

  const finalizeDrag = () => {
    if (!dragStart || !dragEnd || !timeseries) {
      setDragStart(null);
      setDragEnd(null);
      return;
    }
    const allLabels = timeseries.buckets.map((b) => b.label);
    let si = allLabels.indexOf(dragStart);
    let ei = allLabels.indexOf(dragEnd);
    if (si === -1 || ei === -1) { setDragStart(null); setDragEnd(null); return; }
    if (si > ei) [si, ei] = [ei, si];
    if (si === 0 && ei === allLabels.length - 1) setBrushRange(null);
    else setBrushRange([si, ei]);
    setExpandedKey(null);
    setCallDetails(null);
    setDragStart(null);
    setDragEnd(null);
  };

  const shiftRange = (direction: -1 | 1) => {
    const maxIdx = allBuckets.length - 1;
    if (maxIdx < 1) return;
    const curStart = brushRange ? brushRange[0] : 0;
    const curEnd = brushRange ? brushRange[1] : maxIdx;
    const step = brushRange
      ? Math.max(1, Math.floor((curEnd - curStart + 1) / 2))
      : Math.max(1, Math.floor(allBuckets.length / 4));
    let newStart = curStart + direction * step;
    let newEnd = curEnd + direction * step;
    if (!brushRange) {
      if (direction === -1) { newEnd = maxIdx; newStart = Math.max(0, maxIdx - step + 1); }
      else { newStart = 0; newEnd = Math.min(maxIdx, step - 1); }
    }
    if (newStart < 0) { newEnd -= newStart; newStart = 0; }
    if (newEnd > maxIdx) { newStart -= (newEnd - maxIdx); newEnd = maxIdx; }
    newStart = Math.max(0, newStart);
    newEnd = Math.min(maxIdx, newEnd);
    if (newStart === 0 && newEnd === maxIdx) clearSelection();
    else { setBrushRange([newStart, newEnd]); setExpandedKey(null); setCallDetails(null); }
  };

  const handleRowClick = async (rowProvider: string, operation: string) => {
    const key = `${rowProvider}:${operation}`;
    if (expandedKey === key) {
      setExpandedKey(null);
      setCallDetails(null);
      return;
    }
    if (!report?.period_start || !timeseries) return;
    setExpandedKey(key);
    setCallDetails(null);
    setCallDetailsLoading(true);
    try {
      const { start, end } = brushToDateRange(brushRange, period, report.period_start, timeseries.buckets.length);
      const resp = await api.getApiUsageCalls(start, end, rowProvider, operation, undefined, userScope || undefined);
      setCallDetails(resp.rows);
      setCallDetailsTotal(resp.total_count);
    } catch {
      setCallDetails([]);
    } finally {
      setCallDetailsLoading(false);
    }
  };

  const pct = (n: number, total: number) => total > 0 ? `${Math.round((n / total) * 100)}%` : '-';

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">API Usage</h2>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden text-sm">
          {(['hour', 'day', 'month', 'year'] as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => handlePeriodChange(p)}
              className={`px-3 py-1.5 capitalize ${period === p ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
            >
              {p}
            </button>
          ))}
        </div>

        <select
          value={provider}
          onChange={(e) => { setProvider(e.target.value); clearSelection(); }}
          className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          title="Filter by API"
        >
          <option value="">All APIs</option>
          {PROVIDERS.map((p) => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>

        <UsageUserFilter
          value={userScope}
          onChange={(next) => {
            setUserScope(next);
            // The period we are on was anchored on the OLD scope's latest data;
            // reset so the panel re-anchors rather than opening on an empty
            // month for someone who was not working then.
            setPeriodDate(undefined);
            clearSelection();
          }}
        />

        {report?.period_start && (
          <div className="flex items-center gap-1">
            <button onClick={handlePrev} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300" title="Previous period">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[140px] text-center">
              {formatPeriodHeader(report.period_start, period)}
            </span>
            <button
              onClick={handleNext}
              disabled={nextDisabled}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Next period"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Summary cards */}
      {report && report.total_calls > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div className="bg-white dark:bg-gray-800 border rounded-lg px-4 py-3">
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Calls</div>
            <div className="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-0.5">{report.total_calls.toLocaleString()}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 border rounded-lg px-4 py-3">
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Succeeded</div>
            <div className="text-xl font-semibold text-green-700 dark:text-green-300 mt-0.5">
              {report.total_successes.toLocaleString()}
              <span className="text-sm font-normal text-gray-400 dark:text-gray-500 ml-1">{pct(report.total_successes, report.total_calls)}</span>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 border rounded-lg px-4 py-3">
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Errors</div>
            <div className="text-xl font-semibold text-red-600 dark:text-red-400 mt-0.5">
              {report.total_errors.toLocaleString()}
              <span className="text-sm font-normal text-gray-400 dark:text-gray-500 ml-1">{pct(report.total_errors, report.total_calls)}</span>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 border rounded-lg px-4 py-3">
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Rate limited</div>
            <div className="text-xl font-semibold text-amber-600 dark:text-amber-400 mt-0.5">
              {report.total_rate_limited.toLocaleString()}
              <span className="text-sm font-normal text-gray-400 dark:text-gray-500 ml-1">{pct(report.total_rate_limited, report.total_calls)}</span>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded mb-4">{error}</div>
      )}

      {/* Chart */}
      {!loading && hasTimeseries && (
        <div className="bg-white dark:bg-gray-800 border rounded-lg p-4 mb-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Calls {period === 'hour' ? 'per Minute' : period === 'day' ? 'per Hour' : period === 'month' ? 'per Day' : 'per Month'}
          </h3>
          <div className="w-full" style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
                onMouseDown={(e) => { if (e?.activeLabel != null) setDragStart(String(e.activeLabel)); }}
                onMouseMove={(e) => { if (dragStart && e?.activeLabel != null) setDragEnd(String(e.activeLabel)); }}
                onMouseUp={finalizeDrag}
                onMouseLeave={() => { if (dragStart) { setDragStart(null); setDragEnd(null); } }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11 }}
                  interval={chartData.length <= 12 ? 0 : chartData.length <= 30 ? 1 : Math.floor(chartData.length / 15)}
                  tickLine={false}
                />
                <YAxis tick={{ fontSize: 11 }} width={40} tickLine={false} axisLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Line type="monotone" dataKey="call_count" name="Calls" stroke="#6366f1" strokeWidth={2} dot={chartData.length <= 30} />
                <Line type="monotone" dataKey="success_count" name="Succeeded" stroke="#22c55e" strokeWidth={2} dot={chartData.length <= 30} />
                <Line type="monotone" dataKey="error_count" name="Errors" stroke="#ef4444" strokeWidth={2} dot={chartData.length <= 30} />
                <Line type="monotone" dataKey="rate_limited_count" name="Rate limited" stroke="#f59e0b" strokeWidth={2} dot={chartData.length <= 30} />
                {dragStart && dragEnd && (
                  <ReferenceArea x1={dragStart} x2={dragEnd} strokeOpacity={0.3} fill="#6366f1" fillOpacity={0.15} />
                )}
                <Legend verticalAlign="bottom" height={28} iconType="line" iconSize={14} wrapperStyle={{ fontSize: 12 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Range controls */}
          {allBuckets.length > 0 && (
            <div className="flex items-center gap-1.5 mt-3 pt-2 border-t border-gray-100 dark:border-gray-700 flex-wrap">
              <span className="text-xs text-gray-500 dark:text-gray-400 font-medium mr-1">Range:</span>
              <button
                onClick={() => shiftRange(-1)}
                disabled={brushRange != null && brushRange[0] === 0}
                className="inline-flex items-center justify-center w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <select
                value={brushRange ? brushRange[0] : 0}
                onChange={(e) => {
                  const ns = Number(e.target.value);
                  const end = Math.max(ns, brushRange ? brushRange[1] : allBuckets.length - 1);
                  if (ns === 0 && end === allBuckets.length - 1) clearSelection();
                  else { setBrushRange([ns, end]); setExpandedKey(null); setCallDetails(null); }
                }}
                className="text-xs border border-gray-300 dark:border-gray-600 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                {allBuckets.map((b, i) => <option key={i} value={i}>{b.label}</option>)}
              </select>
              <span className="text-xs text-gray-400 dark:text-gray-500">to</span>
              <select
                value={brushRange ? brushRange[1] : allBuckets.length - 1}
                onChange={(e) => {
                  const ne = Number(e.target.value);
                  const start = Math.min(brushRange ? brushRange[0] : 0, ne);
                  if (start === 0 && ne === allBuckets.length - 1) clearSelection();
                  else { setBrushRange([start, ne]); setExpandedKey(null); setCallDetails(null); }
                }}
                className="text-xs border border-gray-300 dark:border-gray-600 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                {allBuckets.map((b, i) => <option key={i} value={i}>{b.label}</option>)}
              </select>
              <button
                onClick={() => shiftRange(1)}
                disabled={brushRange != null && brushRange[1] >= allBuckets.length - 1}
                className="inline-flex items-center justify-center w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
              {brushRange && (
                <button onClick={clearSelection} className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium ml-2 px-2 py-1 rounded border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/30">
                  Reset zoom
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Per-user split — only when looking at everyone; see the module docstring. */}
      {!loading && userScope === ALL_USERS && report && report.by_user.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border rounded-lg overflow-hidden mb-4">
          <div className="px-4 py-2 border-b bg-gray-50 dark:bg-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
            By user
          </div>
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800 border-b">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">User</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Calls</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Share</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Errors</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {report.by_user.map((row) => (
                <tr key={row.user_id ?? 'system'}>
                  <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{row.user_label}</td>
                  <td className="px-4 py-2 text-right font-mono text-sm text-gray-600 dark:text-gray-300">{row.call_count}</td>
                  <td className="px-4 py-2 text-right font-mono text-sm text-gray-500 dark:text-gray-400">{pct(row.call_count, report.total_calls)}</td>
                  <td className="px-4 py-2 text-right font-mono text-sm text-red-600 dark:text-red-400">{row.error_count || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading...</div>
      ) : !report || report.rows.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 border rounded-lg p-8 text-center text-gray-500 dark:text-gray-400">
          No API calls recorded yet
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">API</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Operation</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Calls</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Succeeded</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Errors</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Rate limited</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Results</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Avg time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {report.rows.map((row) => {
                const key = `${row.provider}:${row.operation}`;
                const isExpanded = expandedKey === key;
                return (
                  <React.Fragment key={key}>
                    <tr
                      className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer select-none"
                      onClick={() => handleRowClick(row.provider, row.operation)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <svg
                            className={`w-3.5 h-3.5 text-gray-400 dark:text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                            fill="none" viewBox="0 0 24 24" stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          <span className="text-gray-900 dark:text-gray-100">{providerLabel(row.provider)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-sm text-gray-600 dark:text-gray-300">{row.operation}</td>
                      <td className="px-4 py-3 text-right font-mono text-sm text-gray-600 dark:text-gray-300">{row.call_count}</td>
                      <td className="px-4 py-3 text-right font-mono text-sm text-green-700 dark:text-green-300">
                        {row.success_count}
                        <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">{pct(row.success_count, row.call_count)}</span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm text-red-600 dark:text-red-400">
                        {row.error_count}
                        <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">{pct(row.error_count, row.call_count)}</span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm text-amber-600 dark:text-amber-400">
                        {row.rate_limited_count || '-'}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm text-gray-600 dark:text-gray-300">
                        {row.result_count != null ? row.result_count.toLocaleString() : '-'}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm text-gray-600 dark:text-gray-300">
                        {row.avg_response_time != null ? `${row.avg_response_time.toFixed(2)}s` : '-'}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={8} className="bg-gray-50 dark:bg-gray-800 px-4 py-3">
                          {callDetailsLoading ? (
                            <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">Loading calls...</div>
                          ) : !callDetails || callDetails.length === 0 ? (
                            <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">No calls found in this range</div>
                          ) : (
                            <>
                              {callDetailsTotal > callDetails.length && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                  Showing {callDetails.length} of {callDetailsTotal} calls
                                </div>
                              )}
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-xs text-gray-500 dark:text-gray-400 uppercase">
                                    <th className="text-left py-1.5 pr-3 font-medium">Time</th>
                                    <th className="text-left py-1.5 pr-3 font-medium">User</th>
                                    <th className="text-left py-1.5 pr-3 font-medium">Detail</th>
                                    <th className="text-right py-1.5 pr-3 font-medium">Result</th>
                                    <th className="text-right py-1.5 pr-3 font-medium">Time</th>
                                    <th className="text-right py-1.5 font-medium">Status</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                  {callDetails.map((call) => (
                                    <tr key={call.id} className="hover:bg-gray-100 dark:hover:bg-gray-700">
                                      <td className="py-1.5 pr-3 text-gray-600 dark:text-gray-300 font-mono whitespace-nowrap text-xs">
                                        {new Date(call.created_at).toLocaleTimeString('en-US', { hour12: false })}
                                      </td>
                                      <td className="py-1.5 pr-3 text-gray-600 dark:text-gray-300 text-xs whitespace-nowrap">
                                        {call.user_label ?? '-'}
                                      </td>
                                      <td
                                        className="py-1.5 pr-3 text-gray-600 dark:text-gray-300 max-w-xs truncate text-xs font-mono"
                                        title={call.error ?? call.detail ?? ''}
                                      >
                                        {/* The error is the useful text when there is one — it
                                            is what the person opened this row to read. */}
                                        {call.error ?? call.detail ?? '-'}
                                      </td>
                                      <td className="py-1.5 pr-3 text-right font-mono text-xs text-gray-600 dark:text-gray-300">
                                        {call.result_count != null ? call.result_count : '-'}
                                      </td>
                                      <td className="py-1.5 pr-3 text-right font-mono text-xs text-gray-600 dark:text-gray-300">
                                        {call.response_time != null ? `${call.response_time.toFixed(2)}s` : '-'}
                                      </td>
                                      <td className="py-1.5 text-right text-xs font-mono">
                                        {call.rate_limited ? (
                                          <span className="text-amber-600 dark:text-amber-400 font-medium">limited</span>
                                        ) : call.succeeded ? (
                                          <span className="text-green-700 dark:text-green-300">{call.status_code ?? 'ok'}</span>
                                        ) : (
                                          <span className="text-red-600 dark:text-red-400">{call.status_code ?? 'failed'}</span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
